import { useState } from 'react';
import { Button } from './ui/button';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

/**
 * Botón reutilizable para exportar datos a Excel
 * 
 * @param {string} endpoint - Endpoint de la API (sin /api)
 * @param {object} filters - Filtros actuales a aplicar
 * @param {string} filename - Nombre del archivo (sin extensión ni fecha)
 * @param {string} label - Texto del botón (opcional)
 */
const ExportButton = ({ 
  endpoint, 
  filters = {}, 
  filename = 'export',
  label = 'Exportar'
}) => {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      // Construir query params desde filtros
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });

      // Si el endpoint ya contiene 'export', usarlo tal cual, sino agregar '/export'
      const exportPath = endpoint.includes('export') ? endpoint : `${endpoint}/export`;
      const url = `${exportPath}${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await api.get(url, {
        responseType: 'blob'
      });

      // Crear link de descarga
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Obtener nombre del archivo del header o usar default
      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) downloadFilename = match[1];
      }
      
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Archivo descargado correctamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar los datos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleExport}
      disabled={loading}
      className="gap-2"
      data-testid="export-button"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Download className="h-4 w-4" strokeWidth={1.5} />
      )}
      {label}
    </Button>
  );
};

export default ExportButton;
